/**
 * The voice of the kiosk role: one WebSocket to `/ws/audio/{session}` that carries the microphone up,
 * the reply audio down, and — while it is open — the user's touches and typed words, so the answer is
 * spoken (docs/NEW-UI/protocollo.md §6).
 *
 * The socket is open while the microphone is on, or always with `?speak=1` or Settings' switch. It reopens by itself if
 * it drops while wanted. Words waiting for a muted kiosk (an operator's, a notice: `kiosk.stores.voiceWanted`) open it
 * until they are said or their deadline passes. The text of every turn still arrives on the SSE: the socket is only the voice.
 */
import { describeError, report } from '../core/diagnostics';
import type { AecMode } from '../core/flags';
import type { Kiosk } from '../core/kiosk';
import { createStore } from '../core/store';
import { avDebugEnabled, getAvsyncPersistent } from '../avatar/sync';
import { recordBargeInAck } from '../avatar/sync-telemetry';
import type { SpokenClause, VoiceController, VoiceState } from './controller';
import { startMicrophone as openMicrophone, type Microphone } from './microphone';
import { ReplyPlayback, type PlaybackEvents } from './playback';
import { activityAfter, aecForCanceller, parseVoiceEvent, voiceSocketUrl, type VoiceClientMessage } from './protocol';

/** Past this many bytes waiting to be sent, a microphone frame is dropped: the link is behind. */
const MAX_BUFFERED_BYTES = 64 * 1024;
const RECONNECT_DELAYS_MS = [1500, 3000, 6000, 10000];

/** The parts of the reply audio the voice drives (an interface, so tests can stand in for Web Audio). */
export type Playback = Pick<ReplyPlayback, 'unlock' | 'beginTurn' | 'push' | 'endTurn' | 'firstFrame' | 'stop' | 'clock' | 'dispose'>;

export interface VoiceOptions {
  sessionId: string;
  kiosk: Kiosk;
  aec: AecMode;
  /** The URL chose `aec` (`?aec=`): the backend's declared canceller does not change it. Default: pinned. */
  aecPinned?: boolean;
  /** `?speak=1`: keep the socket open with the microphone off, so typed turns are spoken. */
  keepOpen: boolean;
  WebSocketImpl?: typeof WebSocket;
  location?: Pick<Location, 'protocol' | 'host'>;
  createPlayback?: (events: PlaybackEvents) => Playback;
  startMicrophone?: typeof openMicrophone;
}

export function createVoice(options: VoiceOptions): VoiceController {
  const { sessionId, kiosk } = options;
  let aec = options.aec;
  const aecPinned = options.aecPinned ?? true;
  let keepOpen = options.keepOpen;
  const Socket = options.WebSocketImpl ?? WebSocket;
  const startMic = options.startMicrophone ?? openMicrophone;
  const state = createStore<VoiceState>('voice', { connection: 'off', activity: 'idle', microphone: false, replying: false, error: null });
  const patch = (p: Partial<VoiceState>) => state.set((s) => ({ ...s, ...p }));

  let socket: WebSocket | null = null;
  let mic: Microphone | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  let disposed = false;
  let woken = false; // open for words waiting for it, not for the microphone or keepOpen
  let wakeTimer: ReturnType<typeof setTimeout> | null = null;
  let avatarConnected = false;
  let clearAvatar: (() => void) | null = null;
  const speechListeners = new Set<(clause: SpokenClause) => void>();

  const send = (msg: VoiceClientMessage) => {
    if (socket?.readyState === Socket.OPEN) socket.send(JSON.stringify(msg));
  };
  const isOpen = () => socket?.readyState === Socket.OPEN;
  const wanted = () => !disposed && (state.get().microphone || keepOpen || woken);

  const playback: Playback = (options.createPlayback ?? ((events) => new ReplyPlayback(events)))({
    started: () => send({ type: 'playback_started' }),
    ended: () => {
      patch({ replying: false });
      send({ type: 'playback_ended' });
      release();
    },
    drained: () => patch({ activity: 'idle' }),
    avatarConnected: () => avatarConnected || speechListeners.size > 0, // a face reading the audio holds it too
    spoken: (clause) => speechListeners.forEach((listener) => listener(clause)),
  });

  function sendMicSettings() {
    if (mic) send({ type: 'mic_settings', ...mic.settings });
  }

  function onText(text: string) {
    const event = parseVoiceEvent(text);
    if (!event) {
      report('warn', 'voice', 'dropped an unreadable voice message', text.slice(0, 200));
      return;
    }
    const activity = activityAfter(event);
    if (activity) patch({ activity });
    switch (event.type) {
      case 'assistant_audio_start':
        patch({ replying: true });
        playback.beginTurn(event.avatar_epoch);
        return;
      case 'filler_audio_start':
        patch({ replying: true });
        return;
      case 'filler_audio_end':
        patch({ replying: false }); // the reply's own audio starts it again
        return;
      case 'assistant_audio_end':
        playback.endTurn(event.total_bytes);
        return;
      case 'barge_in_ack': {
        const p = getAvsyncPersistent();
        if (p) recordBargeInAck(p);
        playback.stop('barge');
        clearAvatar?.();
        return;
      }
      case 'recovery_suggestion':
        report('info', 'voice', `backend suggests: ${event.message}`);
        return;
      case 'audio_chain': {
        const declared = aecForCanceller(event.canceller);
        if (aecPinned || declared === null || declared === aec) return;
        report('info', 'voice', `the backend says "${event.canceller}" removes the echo: capturing with aec=${declared}`);
        aec = declared;
        if (mic) reopenMic();
        return;
      }
      default:
        return; // assistant_message: the text comes by SSE
    }
  }

  // The queued audio is here, not on the backend, which may already have let the reply go (and then sends no
  // barge_in_ack): cut it now, then tell the backend to stop what it still synthesizes.
  function silence() {
    playback.stop('stop');
    clearAvatar?.();
    send({ type: 'stop_voice' });
  }

  function openSocket() {
    if (socket || disposed) return;
    patch({ connection: 'connecting' });
    const ws = new Socket(voiceSocketUrl(options.location ?? window.location, sessionId));
    ws.binaryType = 'arraybuffer';
    socket = ws;
    ws.onopen = () => {
      attempts = 0;
      patch({ connection: 'open', error: null });
      sendMicSettings();
      kiosk.useSpokenChannel({
        press: (button, data) => (isOpen() ? (send({ type: 'action', button_id: button, data }), true) : false),
        say: (text) => (isOpen() ? (send({ type: 'text', text }), true) : false),
        silence,
      });
    };
    ws.onmessage = (e: MessageEvent) => {
      if (e.data instanceof ArrayBuffer) playback.push(e.data);
      else if (typeof e.data === 'string') onText(e.data);
    };
    ws.onerror = () => patch({ connection: 'error' });
    ws.onclose = () => {
      if (socket === ws) socket = null;
      kiosk.useSpokenChannel(null);
      patch({ connection: 'off', activity: 'idle', replying: false });
      if (wanted()) {
        const delay = RECONNECT_DELAYS_MS[Math.min(attempts++, RECONNECT_DELAYS_MS.length - 1)]!;
        report('warn', 'voice', `voice connection closed, reopening in ${delay / 1000}s`);
        reconnectTimer = setTimeout(() => { reconnectTimer = null; openSocket(); }, delay);
      }
    };
  }

  function closeSocket() {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    const ws = socket;
    socket = null;
    kiosk.useSpokenChannel(null);
    ws?.close();
    patch({ connection: 'off', activity: 'idle', replying: false });
  }

  function sendPcm(pcm: ArrayBuffer) {
    if (!socket || socket.readyState !== Socket.OPEN) return;
    if (socket.bufferedAmount > MAX_BUFFERED_BYTES) {
      const p = getAvsyncPersistent();
      if (p) p.micSendSkips += 1;
      return;
    }
    socket.send(pcm);
  }

  /** Opens the microphone with the capture `aec` says now; reopens it if `aec` changed while it was opening. */
  async function openMic() {
    const mode = aec;
    try {
      const opened = await startMic(mode, sendPcm);
      if (!state.get().microphone || disposed) {
        opened.stop(); // switched off while the permission prompt was open
        return;
      }
      mic = opened;
      if (mode !== aec) {
        reopenMic(); // the backend declared its canceller while the prompt was open
        return;
      }
      if (avDebugEnabled()) console.debug('[avdbg] mic settings', opened.settings);
      sendMicSettings();
    } catch (e) {
      report('error', 'voice', `microphone not available: ${describeError(e)}`);
      patch({ microphone: false, error: describeError(e) });
      if (!keepOpen) closeSocket();
    }
  }

  function reopenMic() {
    mic?.stop();
    mic = null;
    void openMic();
  }

  /** Words wait for this kiosk: open the voice for them, until they are said or `until` passes. */
  function wake(until: number | null) {
    if (until === null || disposed) return;
    woken = true;
    if (wakeTimer) clearTimeout(wakeTimer);
    wakeTimer = setTimeout(release, Math.max(0, until - Date.now()));
    openSocket();
  }

  /** The waiting words were said, or will not come: close the voice unless something else wants it. */
  function release() {
    if (wakeTimer) clearTimeout(wakeTimer);
    wakeTimer = null;
    if (!woken) return;
    woken = false;
    if (!state.get().microphone && !keepOpen) closeSocket();
  }

  const stopWaking = kiosk.stores.voiceWanted.subscribe(() => wake(kiosk.stores.voiceWanted.get()));
  if (keepOpen) openSocket();

  return {
    state,
    avatar: {
      clock: () => playback.clock(),
      firstFrame: (turn) => playback.firstFrame(turn),
      setAvatarConnected: (connected) => { avatarConnected = connected; },
      onClear: (listener) => { clearAvatar = listener; },
      onSpeech: (listener) => {
        speechListeners.add(listener);
        return () => speechListeners.delete(listener);
      },
    },
    unlockAudio() {
      try {
        playback.unlock();
      } catch (e) {
        report('warn', 'voice', `audio not available: ${describeError(e)}`);
      }
    },
    async setMicrophone(on) {
      if (on === state.get().microphone) return;
      if (!on) {
        mic?.stop();
        mic = null;
        // muted mid-sentence: not listening any more, and the backend drops what it heard so far
        patch({ microphone: false, ...(state.get().activity === 'listening' ? { activity: 'idle' as const } : {}) });
        if (keepOpen || woken) send({ type: 'mic_off' });
        else closeSocket();
        return;
      }
      patch({ microphone: true, error: null });
      openSocket();
      await openMic();
    },
    setKeepOpen(on) {
      if (on === keepOpen || disposed) return;
      keepOpen = on;
      if (on) openSocket();
      else if (!state.get().microphone && !woken) closeSocket();
    },
    stopSpeaking: silence,
    dispose() {
      disposed = true;
      stopWaking();
      if (wakeTimer) clearTimeout(wakeTimer);
      mic?.stop();
      mic = null;
      closeSocket();
      playback.dispose();
    },
  };
}
